import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import client from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { OpeningHoursEditor } from "@/components/OpeningHoursEditor";
import { FeedPathTag } from "@/components/FeedPathTag";
import { ADDABLE_SERVICE_TYPES } from "@/lib/serviceTypes";
import { Pencil, Save, Plus, Trash2 } from "lucide-react";

const CONTACT_FEED_PATHS = {
  email: "Dealer.Services.Service.Contacts.Email (XML feed)",
  telephone: "Dealer.Services.Service.Contacts.Telephone (XML feed)",
  mobile: "Dealer.Services.Service.Contacts.Mobile (XML feed)",
  fax: "Dealer.Services.Service.Contacts.Fax (XML feed)",
};

function Field({ label, feedPath, children }) {
  return (
    <div>
      <Label className="data-label mb-1 flex items-center gap-1">
        {label}
        <FeedPathTag path={feedPath} />
      </Label>
      {children}
    </div>
  );
}

/** Services tabbed card — contacts + opening hours per service type, plus
 * the branch-level opening-hours note at the bottom (not tied to any single
 * service type, so it can't reuse the per-service `editingId` edit state
 * above).
 *
 * Two separate permissions: `canManage` (dealer.services.edit) covers
 * editing a service's contacts and opening hours — a dealer's own Power
 * Content Manager has it. `canAddRemove` (dealer.services.manage) covers
 * adding and removing service records and is MHQ-side only.
 *
 * Add/Remove sit next to the tab strip and only
 * appear while a tab is in its own Edit mode (`editingId`) — there's no
 * separate "manage mode" toggle, this reuses the per-tab Edit affordance
 * that already exists here. "Remove" always targets the tab currently being
 * edited. Sales/Service can never be added or removed — see
 * docs/specs/bulk-update-dealers-20260904.md §4 and backend
 * ADDABLE_SERVICE_TYPES (models.py). */
export function ServicesCard({ services, onServiceSaved, onServicesChanged, branch, onBranchSaved, dealerId, canManage = false, canAddRemove = false }) {
  const { t } = useI18n();
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(services[0]?.id);
  const [mutating, setMutating] = useState(false);

  const [noteEditing, setNoteEditing] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");
  const [noteSaving, setNoteSaving] = useState(false);

  // Keep the active tab valid as services are added/removed underneath it.
  useEffect(() => {
    if (!services.some((s) => s.id === activeTab)) {
      setActiveTab(services[0]?.id);
    }
  }, [services, activeTab]);

  const editingService = services.find((s) => s.id === editingId);
  const missingTypes = ADDABLE_SERVICE_TYPES.filter((type) => !services.some((s) => s.type === type));
  const canRemoveEditing = editingService && ADDABLE_SERVICE_TYPES.includes(editingService.type);

  const addService = async (type) => {
    setMutating(true);
    try {
      const { data } = await client.post(`/dealers/${dealerId}/services`, { type });
      onServicesChanged((prev) => [...prev, data]);
      setActiveTab(data.id);
      start(data); // land in Edit mode on the new tab so the inherited data is visible right away
      toast.success(t("dealership.saved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add service");
    } finally {
      setMutating(false);
    }
  };

  const removeEditingService = async () => {
    if (!editingService) return;
    if (!window.confirm(`Remove ${t(`serviceTypes.${editingService.type}`)} and all its contact/hours data?`)) return;
    setMutating(true);
    try {
      await client.delete(`/dealers/${dealerId}/services/${editingService.type}`);
      onServicesChanged((prev) => prev.filter((s) => s.id !== editingService.id));
      setEditingId(null);
      toast.success(t("dealership.saved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove service");
    } finally {
      setMutating(false);
    }
  };

  const startNote = () => {
    setNoteDraft(branch.opening_hours_note || "");
    setNoteEditing(true);
  };

  const saveNote = async () => {
    setNoteSaving(true);
    try {
      const { data } = await client.patch(`/branches/${branch.id}`, { opening_hours_note: noteDraft });
      onBranchSaved(data);
      setNoteEditing(false);
      toast.success(t("dealership.saved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setNoteSaving(false);
    }
  };

  const start = (svc) => {
    setEditingId(svc.id);
    setDraft({
      contacts: { ...svc.contacts },
      opening_hours: JSON.parse(JSON.stringify(svc.opening_hours || [])),
    });
  };

  const save = async (svcId) => {
    setSaving(true);
    try {
      const { data } = await client.patch(`/services/${svcId}`, draft);
      onServiceSaved(data);
      setEditingId(null);
      toast.success(t("dealership.saved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="mb-6 p-6" data-testid="services-card">
      <div className="mb-4">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold">{t("dealership.services")}</h2>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">{t("dealership.servicesSub")}</p>
      </div>

      {services.length === 0 && <p className="text-sm text-muted-foreground">No services configured.</p>}

      {services.length > 0 && (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          {/* Stacked on narrow viewports, side-by-side from `sm` up, so the
              Add/Remove buttons don't squeeze the tab strip's own wrapping.
              TabsList's base class (tabs.jsx) is `h-9` — a *fixed* height —
              so even with `flex-wrap` the gray background never grew for a
              second row of tabs; overflow tabs spilled out below it,
              unstyled, colliding with whatever came next. `h-auto` lets the
              box grow with however many rows it actually wraps to. */}
          <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
            <TabsList className="h-auto w-full flex-wrap gap-1 sm:w-auto">
              <TooltipProvider delayDuration={300}>
                {services.map((s) => (
                  <Tooltip key={s.id}>
                    <TooltipTrigger asChild>
                      {/* Tabs and Tooltip are both stateful Radix primitives that
                          write their own `data-state` onto their trigger node;
                          the span isolates Tooltip's onto a neutral wrapper so
                          it doesn't clobber the Tabs trigger's active/inactive
                          state (and its styling) underneath. */}
                      <span className="inline-flex">
                        <TabsTrigger value={s.id} data-testid={`service-tab-${s.type}`}>
                          {t(`serviceTypes.${s.type}`)}
                        </TabsTrigger>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>{s.type}</TooltipContent>
                  </Tooltip>
                ))}
              </TooltipProvider>
            </TabsList>

            {canAddRemove && editingId && (
              <div className="flex justify-end gap-1.5">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      data-testid="add-service-btn"
                      variant="outline"
                      size="sm"
                      className="gap-1.5"
                      disabled={mutating || missingTypes.length === 0}
                    >
                      <Plus className="h-3.5 w-3.5" />
                      {t("common.add")}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {missingTypes.map((type) => (
                      <DropdownMenuItem key={type} data-testid={`add-service-${type}`} onClick={() => addService(type)}>
                        {t(`serviceTypes.${type}`)}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button
                  data-testid="remove-service-btn"
                  variant="outline"
                  size="sm"
                  className="gap-1.5 text-destructive"
                  disabled={mutating || !canRemoveEditing}
                  onClick={removeEditingService}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  {t("common.remove")}
                </Button>
              </div>
            )}
          </div>
          {services.map((s) => {
            const isEditing = editingId === s.id;
            const view = isEditing ? draft : s;
            const contact = (k) => (isEditing ? draft.contacts[k] : s.contacts?.[k] || "");
            return (
              <TabsContent key={s.id} value={s.id} className="space-y-6">
                <div className="flex items-center justify-between">
                  <p className="data-label">{t("dealership.contacts")}</p>
                  {!isEditing ? (
                    canManage && (
                      <Button data-testid={`edit-service-${s.type}`} size="sm" variant="ghost" className="gap-1.5" onClick={() => start(s)}>
                        <Pencil className="h-3.5 w-3.5" />
                        {t("common.edit")}
                      </Button>
                    )
                  ) : (
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => setEditingId(null)}>
                        {t("common.cancel")}
                      </Button>
                      <Button data-testid={`save-service-${s.type}`} size="sm" className="gap-1.5"
                        onClick={() => save(s.id)} disabled={saving}>
                        <Save className="h-3.5 w-3.5" />
                        {t("common.save")}
                      </Button>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {["email", "telephone", "mobile", "fax"].map((k) => (
                    <Field key={k} label={t(`dealership.${k}`)} feedPath={CONTACT_FEED_PATHS[k]}>
                      <Input
                        data-testid={`service-${s.type}-${k}`}
                        disabled={!isEditing || saving}
                        value={contact(k)}
                        onChange={(e) => setDraft((d) => ({ ...d, contacts: { ...d.contacts, [k]: e.target.value } }))}
                      />
                    </Field>
                  ))}
                </div>
                <div>
                  <p className="data-label mb-2 flex items-center gap-1">
                    {t("dealership.openingHours")}
                    <FeedPathTag path="Dealer.Services.Service.OpeningHours (XML feed)" />
                  </p>
                  <OpeningHoursEditor
                    value={view.opening_hours}
                    disabled={!isEditing || saving}
                    onChange={(v) => setDraft((d) => ({ ...d, opening_hours: v }))}
                    testIdPrefix={`oh-${s.type}`}
                  />
                </div>
              </TabsContent>
            );
          })}
        </Tabs>
      )}

      {branch && (
        <div className="mt-6 border-t border-border pt-6">
          <div className="mb-2 flex items-center justify-between">
            <p className="data-label flex items-center gap-1">
              {t("dealership.openingHoursNote")}
              <FeedPathTag path="Dealer.OpeningHours.Note" />
            </p>
            {!noteEditing ? (
              canManage && (
                <Button data-testid="edit-opening-hours-note-btn" size="sm" variant="ghost" className="gap-1.5" onClick={startNote}>
                  <Pencil className="h-3.5 w-3.5" />
                  {t("common.edit")}
                </Button>
              )
            ) : (
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => setNoteEditing(false)}>
                  {t("common.cancel")}
                </Button>
                <Button data-testid="save-opening-hours-note-btn" size="sm" className="gap-1.5"
                  onClick={saveNote} disabled={noteSaving}>
                  <Save className="h-3.5 w-3.5" />
                  {t("common.save")}
                </Button>
              </div>
            )}
          </div>
          <Input data-testid="branch-opening-hours-note" disabled={!noteEditing || noteSaving}
            value={noteEditing ? noteDraft : branch.opening_hours_note || ""}
            onChange={(e) => setNoteDraft(e.target.value)} />
        </div>
      )}
    </Card>
  );
}
